import { faWindowClose } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  createContext,
  FC,
  ReactNode,
  RefObject,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { Rnd } from 'react-rnd';
import { Button } from 'src/components/button';
import { Stack } from 'src/components/stack';
import { Colors } from 'src/styles';
import styled from 'styled-components';

const borderRadius = '0.6rem';

const Frame = styled.div`
  position: absolute;
  z-index: 9;
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-rows: auto 1fr;
  box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.5);
  border-radius: ${borderRadius};
`;

const Title = styled.div`
  color: white;
  background-color: ${Colors.brand};
  border-top-left-radius: ${borderRadius};
  border-top-right-radius: ${borderRadius};
  padding: 0.5rem 1rem;
  cursor: move;
`;

const Content = styled.div`
  background-color: white;
  border-bottom-left-radius: ${borderRadius};
  border-bottom-right-radius: ${borderRadius};
  padding: 0.5rem 1rem;
  overflow: auto;
`;

const triggerMouseEvent = (element: Node, eventType: string) => {
  const mouseEvent = document.createEvent('MouseEvents');
  mouseEvent.initEvent(eventType, true, true);
  element.dispatchEvent(mouseEvent);
};

// see https://github.com/react-grid-layout/react-draggable/issues/363#issuecomment-947751127
// sadly react-rnd doesn't handle this internally
function useViewportResizeHandler() {
  const dragHandlerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onViewportResize = (e: UIEvent) => {
      if (!dragHandlerRef.current) return;
      triggerMouseEvent(dragHandlerRef.current, 'mouseover');
      triggerMouseEvent(dragHandlerRef.current, 'mousedown');
      triggerMouseEvent(document, 'mousemove');
      triggerMouseEvent(dragHandlerRef.current, 'mouseup');
      triggerMouseEvent(dragHandlerRef.current, 'click');
    };
    window.addEventListener('resize', onViewportResize);
    return () => window.removeEventListener('resize', onViewportResize);
  }, []);

  return dragHandlerRef;
}

// we need to share a ref to our window, so it can be scrolled correctly when a modal is open
export type WindowRef = RefObject<HTMLDivElement>;

const WindowRefContext = createContext<WindowRef | null>(null);

export const WindowRefProvider = WindowRefContext.Provider;

export function useWindowRef() {
  return useContext(WindowRefContext);
}

type Props = {
  title: string;
  onClose: () => void;
  children: ReactNode;
};

export const Window: FC<Props> = ({ children, title, onClose }) => {
  const windowRef = useWindowRef();
  const dragHandlerRef = useViewportResizeHandler();
  const width = 200;
  const height = 200;
  const x = window.innerWidth - width - 20;
  const y = window.innerHeight - height - 20;

  return (
    <Rnd
      style={{ zIndex: 5 }}
      default={{ width, height, x, y }}
      bounds="body"
      minWidth={200}
      minHeight={100}
      dragHandleClassName="drag-handler"
      resizeHandleStyles={{
        left: { cursor: 'ew-resize' },
        right: { cursor: 'ew-resize' },
        top: { cursor: 'ns-resize' },
        bottom: { cursor: 'ns-resize' },
        topLeft: { cursor: 'nwse-resize' },
        topRight: { cursor: 'nesw-resize' },
        bottomLeft: { cursor: 'nesw-resize' },
        bottomRight: { cursor: 'nwse-resize' },
      }}
    >
      <Frame ref={windowRef}>
        <Title ref={dragHandlerRef} className="drag-handler">
          <Stack flow="column" gap={1} templateColumns="1fr auto">
            <span>{title}</span>
            <Button mode="icon-inverse" onClick={onClose}>
              <FontAwesomeIcon icon={faWindowClose} aria-label="Close" />
            </Button>
          </Stack>
        </Title>

        <Content>{children}</Content>
      </Frame>
    </Rnd>
  );
};
